/**
 * Product finder: dropdown UI, dependency, and product filtering.
 *
 * Two different things (do not mix):
 * - Entry handle: metaobject entry's system.handle (e.g. "outside-wood-satin"). Used to link products
 *   to entries. Stored on each product card as data-product-finder-handles (comma-separated entry handles).
 * - Entry values: category/display values in the entry (area_of_use, aspect, etc. — e.g. "Satin", "Outside").
 *   Used to match user's dropdown selection. Stored in entries JSON and in dropdown options.
 *
 * Flow: User selects values → we find entries whose values match → we get those entries' handles →
 * we show products whose data-product-finder-handles contains any of those handles.
 * Field order is dynamic: follows the order of dropdown blocks in the theme (DOM order).
 */

/** @typedef {{ handle: string, [key: string]: string|null }} ProductFinderEntry */

export class ProductFinderComponent extends HTMLElement {
  /** Product finder metaobject entries (from JSON). Each has handle + field values (area_of_use, aspect, etc.). */
  /** @type {ProductFinderEntry[]} */
  #entries = [];
  /** Current dropdown selections: { fieldKey: selectedValue }. Cleared when a prior dropdown changes. */
  /** @type {Record<string, string>} */
  #state = {};
  /** Stored reference so we can remove the document click listener on disconnect. */
  /** @type {((e: Event) => void) | null} */
  #outsideClickHandler = null;

  connectedCallback() {
    this.#loadEntries();
    this.#state = this.#buildInitialState();
    this.#bindDropdowns();
    this.#bindClearButton();
    this.#refreshAfterStateChange();
    this.addEventListener('product-finder-change', this.#onSelectionChange);
  }

  disconnectedCallback() {
    this.removeEventListener('product-finder-change', this.#onSelectionChange);
    if (this.#outsideClickHandler) document.removeEventListener('click', /** @type {EventListener} */ (this.#outsideClickHandler));
  }

  /** Load product finder entries from the JSON script rendered by product-finder-entries-data.liquid. */
  #loadEntries() {
    const script = document.getElementById('product-finder-entries-data');
    if (!script?.textContent) return;
    try {
      this.#entries = JSON.parse(script.textContent.trim());
    } catch {
      this.#entries = [];
    }
  }

  /** Field order: DOM order of [data-product-finder-field] (matches block order in theme customizer). */
  #getFieldOrder() {
    return Array.from(this.querySelectorAll('[data-product-finder-field]'))
      .map((w) => w.getAttribute('data-product-finder-field'))
      .filter((x) => typeof x === 'string');
  }

  /** One empty string per field so we can track selections and clear later fields when one changes. */
  #buildInitialState() {
    return Object.fromEntries(this.#getFieldOrder().map((f) => [f, '']));
  }

  /** Bind trigger click (toggle list), option click (set value + dispatch change), outside click (close), Escape (close). */
  #bindDropdowns() {
    this.querySelectorAll('[data-product-finder-field]').forEach((wrap) => {
      const trigger = wrap.querySelector('[data-product-finder-trigger]');
      const list = wrap.querySelector('[data-product-finder-list]');
      const valueEl = wrap.querySelector('[data-product-finder-value]');
      const field = wrap.getAttribute('data-product-finder-field');
      if (!trigger || !list || !valueEl) return;

      // Toggle this dropdown list; close others when opening.
      trigger.addEventListener('click', (/** @type {Event} */ e) => {
        e.stopPropagation();
        const listEl = /** @type {HTMLElement} */ (list);
        if (!listEl.hidden) {
          listEl.hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          this.#closeAllDropdowns();
          listEl.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      // On option click: update display, close list, dispatch product-finder-change so state + filtering run.
      list.querySelectorAll('[data-value]').forEach((li) => {
        li.addEventListener('click', (/** @type {Event} */ e) => {
          e.stopPropagation();
          if (li.getAttribute('aria-disabled') === 'true') return;
          const val = li.getAttribute('data-value') || '';
          valueEl.textContent = (li.textContent || '').trim();
          (/** @type {HTMLElement} */ (list)).hidden = true;
          trigger.setAttribute('aria-expanded', 'false');
          wrap.dispatchEvent(new CustomEvent('product-finder-change', { detail: { field: field || '', value: val }, bubbles: true }));
        });
      });
    });

    // Close all dropdowns when clicking outside any [data-product-finder-field].
    this.#outsideClickHandler = (/** @type {Event} */ e) => {
      const target = e.target instanceof Node ? e.target : null;
      const insideAnyDropdown = target && Array.from(this.querySelectorAll('[data-product-finder-field]')).some((wrap) => wrap.contains(target));
      if (!insideAnyDropdown) this.#closeAllDropdowns();
    };
    document.addEventListener('click', this.#outsideClickHandler);

    // Close all dropdowns on Escape.
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const lists = this.querySelectorAll('[data-product-finder-list]');
      if (Array.from(lists).some((el) => !(/** @type {HTMLElement} */ (el)).hidden)) this.#closeAllDropdowns();
    });
  }

  /** Hide every dropdown list and set aria-expanded false on triggers. */
  #closeAllDropdowns() {
    this.querySelectorAll('[data-product-finder-field]').forEach((wrap) => {
      const list = wrap.querySelector('[data-product-finder-list]');
      const trigger = wrap.querySelector('[data-product-finder-trigger]');
      if (list) (/** @type {HTMLElement} */ (list)).hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  /** Re-run dependency, segment classes, product filter, and clear button visibility. Call after state changes. */
  #refreshAfterStateChange() {
    this.#applyDependency();
    this.#updateSegmentEmptyState();
    this.#updateSegmentDisabledState();
    this.#filterProducts();
    this.#updateClearButtonVisibility();
  }

  /** When a dropdown option is chosen: update state, clear all later fields, then refresh. */
  #onSelectionChange = (/** @type {CustomEvent<{ field?: string; value?: string }>} */ e) => {
    const { field, value } = e.detail || {};
    if (field) {
      this.#state[field] = value || '';
      const order = this.#getFieldOrder();
      const idx = order.indexOf(field);
      if (idx >= 0) order.slice(idx + 1).forEach((f) => (this.#state[f] = ''));
    }
    this.#refreshAfterStateChange();
  };

  #bindClearButton() {
    const btn = this.querySelector('[data-product-finder-clear]');
    if (btn) btn.addEventListener('click', (/** @type {Event} */ e) => { e.stopPropagation(); this.#clearFilters(); });
  }

  /** Reset all selections and display placeholders; refresh. */
  #clearFilters() {
    this.#getFieldOrder().forEach((k) => (this.#state[k] = ''));
    this.querySelectorAll('[data-product-finder-field]').forEach((wrap) => {
      const valueEl = wrap.querySelector('[data-product-finder-value]');
      const trigger = wrap.querySelector('[data-product-finder-trigger]');
      if (valueEl && trigger) valueEl.textContent = trigger.getAttribute('aria-label') || '';
    });
    this.#refreshAfterStateChange();
  }

  /** Class added to dropdown and matching text segment when that dropdown has no selection. Use for hide/strike-through. */
  static get SEGMENT_EMPTY_CLASS_PREFIX() {
    return 'product-finder-segment-empty-';
  }

  /** Common class for any empty segment (use in CSS to target all at once). */
  static get SEGMENT_EMPTY_CLASS() {
    return 'product-finder-segment-empty';
  }

  /** Class added when that dropdown has all options disabled (e.g. no matching entries). Use for hide/strike-through. */
  static get SEGMENT_ALL_DISABLED_CLASS_PREFIX() {
    return 'product-finder-segment-all-disabled-';
  }

  /** Common class for any all-disabled segment (use in CSS to target all at once). */
  static get SEGMENT_ALL_DISABLED_CLASS() {
    return 'product-finder-segment-all-disabled';
  }

  /** Remove all segment classes with given prefix (and optional common class) so they don't stick when selection changes. */
  /** @param {string} prefix @param {string} [commonClass] */
  #clearSegmentClassesByPrefix(prefix, commonClass) {
    const els = this.querySelectorAll('[data-product-finder-field], [data-product-finder-segment]');
    els.forEach((el) => {
      Array.from(el.classList).forEach((c) => {
        if (c.startsWith(prefix)) el.classList.remove(c);
      });
      if (commonClass) el.classList.remove(commonClass);
    });
  }

  /** Add/remove product-finder-segment-empty-{field} and common product-finder-segment-empty on dropdowns and text segments when value is empty. */
  #updateSegmentEmptyState() {
    this.#clearSegmentClassesByPrefix(ProductFinderComponent.SEGMENT_EMPTY_CLASS_PREFIX, ProductFinderComponent.SEGMENT_EMPTY_CLASS);
    const order = this.#getFieldOrder();
    const commonClass = ProductFinderComponent.SEGMENT_EMPTY_CLASS;
    order.forEach((field) => {
      const emptyClass = ProductFinderComponent.SEGMENT_EMPTY_CLASS_PREFIX + field;
      const hasValue = Boolean(this.#state[field]);
      const wrap = this.querySelector(`[data-product-finder-field="${field}"]`);
      if (wrap) {
        wrap.classList.toggle(emptyClass, !hasValue);
        wrap.classList.toggle(commonClass, !hasValue);
      }
      this.querySelectorAll(`[data-product-finder-segment="${field}"]`).forEach((el) => {
        el.classList.toggle(emptyClass, !hasValue);
        el.classList.toggle(commonClass, !hasValue);
      });
    });
  }

  /** Add/remove product-finder-segment-all-disabled-{field} and common class when all options in that dropdown are disabled. */
  #updateSegmentDisabledState() {
    this.#clearSegmentClassesByPrefix(ProductFinderComponent.SEGMENT_ALL_DISABLED_CLASS_PREFIX, ProductFinderComponent.SEGMENT_ALL_DISABLED_CLASS);
    const order = this.#getFieldOrder();
    const commonClass = ProductFinderComponent.SEGMENT_ALL_DISABLED_CLASS;
    order.forEach((field) => {
      const disabledClass = ProductFinderComponent.SEGMENT_ALL_DISABLED_CLASS_PREFIX + field;
      const wrap = this.querySelector(`[data-product-finder-field="${field}"]`);
      let allDisabled = false;
      if (wrap && order.indexOf(field) !== 0) {
        const options = wrap.querySelectorAll('[data-product-finder-list] [data-value]');
        const n = options.length;
        allDisabled = n > 0 && Array.from(options).every((li) => li.getAttribute('aria-disabled') === 'true');
      }
      if (wrap) {
        wrap.classList.toggle(disabledClass, allDisabled);
        wrap.classList.toggle(commonClass, allDisabled);
      }
      this.querySelectorAll(`[data-product-finder-segment="${field}"]`).forEach((el) => {
        el.classList.toggle(disabledClass, allDisabled);
        el.classList.toggle(commonClass, allDisabled);
      });
    });
  }

  /** Show clear button only when at least one dropdown has a selection. */
  #updateClearButtonVisibility() {
    const btn = /** @type {HTMLElement | null} */ (this.querySelector('[data-product-finder-clear]'));
    if (!btn) return;
    const has = this.#getFieldOrder().some((f) => this.#state[f]);
    btn.hidden = !has;
    btn.classList.toggle('product-finder__clear--visible', has);
  }

  /** Normalize for comparison: trim, lower case. Used so "Satin" and "satin" (or title vs handle) match. */
  /** @param {string|null|undefined} v */
  #valueForCompare(v) {
    return v == null || v === '' ? '' : String(v).trim().toLowerCase();
  }

  /** Add both normalized value and slug (spaces → hyphens) so dropdown options match entries whether they use title or handle. */
  /** @param {Set<string>} set @param {string|null|undefined} value */
  #addAllowedValues(set, value) {
    if (value == null || value === '') return;
    const norm = this.#valueForCompare(value);
    set.add(norm);
    set.add(norm.replace(/\s+/g, '-'));
  }

  /** Entries whose field values match current state (user selections). Used to get entry handles for filtering products. */
  #getMatchingEntries() {
    return this.#entries.filter((entry) => {
      for (const key of this.#getFieldOrder()) {
        const stateVal = this.#state[key];
        if (!stateVal) continue;
        const entryVal = entry[key];
        if (entryVal == null || this.#valueForCompare(entryVal) !== this.#valueForCompare(stateVal)) return false;
      }
      return true;
    });
  }

  /** All distinct values for this field in entries that match current preceding selections. Used to enable/disable list options. */
  /** @param {string} field */
  #getVisibleOptionValuesForField(field) {
    const order = this.#getFieldOrder();
    const idx = order.indexOf(field);
    if (idx < 0) return new Set();
    // Restrict to entries that match all preceding dropdown selections.
    let entries = this.#entries;
    for (let i = 0; i < idx; i++) {
      const prevKey = order[i];
      if (prevKey === undefined) continue;
      const prev = this.#state[prevKey];
      if (!prev) continue;
      entries = entries.filter((e) => e[prevKey] != null && this.#valueForCompare(e[prevKey]) === this.#valueForCompare(prev));
    }
    const set = new Set();
    entries.forEach((e) => this.#addAllowedValues(set, e[field]));
    return set;
  }

  /** For each dropdown: enable only options in allowed set; clear selection if it became invalid; keep triggers always enabled. */
  #applyDependency() {
    const order = this.#getFieldOrder();
    order.forEach((field) => {
      const wrap = this.querySelector(`[data-product-finder-field="${field}"]`);
      if (!wrap) return;
      const trigger = wrap.querySelector('[data-product-finder-trigger]');
      const list = wrap.querySelector('[data-product-finder-list]');
      const valueEl = wrap.querySelector('[data-product-finder-value]');
      if (!trigger || !list || !valueEl) return;

      const idx = order.indexOf(field);
      const isFirst = idx === 0;
      const allowed = this.#getVisibleOptionValuesForField(field);
      const canShowOptions = isFirst || allowed.size > 0; // First dropdown always has options; others need at least one matching entry.
      const placeholder = trigger.getAttribute('aria-label') || '';
      if (!this.#state[field]) valueEl.textContent = placeholder;

      // Triggers are always enabled (no disabling the button).
      trigger.removeAttribute('disabled');
      trigger.setAttribute('aria-disabled', 'false');
      wrap.removeAttribute('data-product-finder-dropdown-disabled');

      const currentVal = this.#valueForCompare(this.#state[field] || '');
      let currentOptionAllowed = false;

      // Enable only options that are in allowed (valid for current preceding selections); mark selected; track if current selection is still valid.
      list.querySelectorAll('[data-value]').forEach((li) => {
        const val = this.#valueForCompare(li.getAttribute('data-value') || '');
        const ok = canShowOptions && allowed.has(val);
        const sel = val === currentVal;
        li.setAttribute('data-product-finder-disabled', ok ? 'false' : 'true');
        li.setAttribute('aria-disabled', ok ? 'false' : 'true');
        if (sel) {
          li.setAttribute('data-product-finder-selected', 'true');
          li.setAttribute('aria-selected', 'true');
          currentOptionAllowed = ok;
        } else {
          li.removeAttribute('data-product-finder-selected');
          li.setAttribute('aria-selected', 'false');
        }
      });

      // Clear this field if it's not first and either there are no valid options or current selection is no longer in allowed.
      if (!isFirst && (!canShowOptions || (currentVal && !currentOptionAllowed))) {
        this.#state[field] = '';
        valueEl.textContent = placeholder;
      }
    });
  }

  /** Show cards whose data-product-finder-handles includes any matching entry handle; hide others. Show "no products" when selection yields zero. */
  #filterProducts() {
    const container = this.querySelector('.product-finder__collection');
    if (!container) return;
    const order = this.#getFieldOrder();
    const hasAny = order.some((f) => this.#state[f]);
    const matching = this.#getMatchingEntries();
    const handles = new Set(matching.map((e) => String(e.handle).toLowerCase())); // Entry handles that match current selection.

    let visibleCount = 0;
    container.querySelectorAll('product-card[data-product-finder-handles], product-card').forEach((card) => {
      const raw = card.getAttribute('data-product-finder-handles'); // Comma-separated entry handles this product is linked to.
      const cardHandles = raw ? raw.split(',').map((h) => h.trim().toLowerCase()).filter(Boolean) : [];
      // Show card when no filters are set, or when at least one of its entry handles is in the matching set.
      const show = !hasAny || (cardHandles.length > 0 && handles.size > 0 && cardHandles.some((h) => handles.has(h)));
      if (show) visibleCount++;
      const wrapper = /** @type {HTMLElement} */ (card.closest('slideshow-slide') || card.closest('.resource-list__item') || card);
      wrapper.hidden = !show;
    });

    // Show "no products found" only when user has a selection and zero products are visible.
    const emptyEl = /** @type {HTMLElement | null} */ (container.querySelector('[data-product-finder-empty]'));
    if (emptyEl) emptyEl.hidden = !(hasAny && visibleCount === 0);
  }
}

if (!customElements.get('product-finder')) {
  customElements.define('product-finder', ProductFinderComponent);
}

